import React, { useRef, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { yup } from 'schemas/validation'
import { TIMER_MASK_CHAR } from 'constants/ui/timers'
import KEY_CODES from 'constants/keyCodes'
import TextInputMask from '../TextInputMask'

import { beforeMaskedValueChange, getMaskedValue, getRawValue } from './utils'
import withStyle from './TimerInput.style.js'

const DISABLED_KEY_CODES = new Set([
  KEY_CODES.END,
  KEY_CODES.HOME,
  KEY_CODES.LEFT_ARROW,
  KEY_CODES.UP_ARROW,
  KEY_CODES.RIGHT_ARROW,
  KEY_CODES.DOWN_ARROW,
])

const TimerInput = ({ onChange, value = 0, mask, rules, ...fieldProps }) => {
  const ref = useRef()
  const [maskedValue, setMaskedValue] = useState(getMaskedValue(value, mask))

  // don't let the user change cursor position
  useEffect(() => {
    if (ref.current) {
      ref.current.addEventListener('keydown', (event) => {
        if (DISABLED_KEY_CODES.has(event.keyCode)) {
          event.preventDefault()
        }
      })
    }
  }, [ref])

  const setCursorToEnd = () => {
    // delays execution until input mask component handles their events
    window.requestAnimationFrame(() =>
      ref.current.setSelectionRange(mask.length, mask.length)
    )
  }

  // set the maskedValue on change
  const handleChange = (newMaskedValue) => {
    const currentMaskedValue = getMaskedValue(value, mask)

    if (currentMaskedValue !== newMaskedValue) {
      setMaskedValue(newMaskedValue)
    }
  }

  // finally call onChange when blurring out of field
  const handleBlur = (event) => {
    const reducedValue = getRawValue(event.target.value, mask)

    if (reducedValue !== value) {
      onChange(reducedValue)
      setMaskedValue(getMaskedValue(reducedValue, mask))
    }
  }

  return (
    <TextInputMask
      {...fieldProps}
      rules={rules}
      value={maskedValue}
      onChange={handleChange}
      ref={ref}
      alwaysShowMask
      beforeMaskedValueChange={beforeMaskedValueChange(mask)}
      maskChar={TIMER_MASK_CHAR}
      mask={mask}
      onMouseDown={setCursorToEnd}
      onBlur={handleBlur}
      autoComplete="off"
      data-testid="timer-input"
    />
  )
}

TimerInput.defaultProps = {
  rules: yup.string(),
}

TimerInput.propTypes = {
  /** See field component for more props */
  className: PropTypes.string,
  onChange: PropTypes.func,
  value: PropTypes.number,
  mask: PropTypes.string.isRequired,
  rules: PropTypes.object,
}

export default withStyle(TimerInput)
